import { type ElementType } from "react";
import { typography, TypographyVariantProps } from "./typography.variants";

type AsProp<C extends React.ElementType> = {
  as?: C;
};

type PropsToOmit<C extends React.ElementType, P> = keyof (AsProp<C> & P);

type PolymorphicComponentProp<
  C extends React.ElementType,
  Props = Record<string, unknown>
> = React.PropsWithChildren<Props & AsProp<C>> &
  Omit<React.ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>;

export type UseTypographyProps<C extends React.ElementType> =
  PolymorphicComponentProp<
    C,
    TypographyVariantProps & {
      secondColor?: string;
    }
  >;

export const useTypography = <C extends ElementType = "p">(
  props: UseTypographyProps<C>
) => {
  const {
    className = "",
    as = "p",
    fontSize = "body-md",
    fontFamily = "roboto",
    fontWeight,
    lineHeight,
    spacing,
    uppercase = false,
    italic = false,
    ...componentProps
  } = props;

  const styles = typography({
    fontSize,
    fontFamily,
    fontWeight,
    lineHeight,
    uppercase,
    italic,
    spacing,
  });

  const Component = as || ("p" as C);

  return {
    className,
    styles,
    Component,
    ...componentProps,
  };
};
